import { memo, useState } from 'react';

import { useConversation } from 'hooks/conversation';
import { useView } from 'hooks/view';
import TypingIndicator from 'components/TypingIndicator/TypingIndicator';
import { htmlId } from 'utils/html';

import { DEFAULT_TYPING_ANIMATION_DURATION } from 'components/TypingIndicator/constants';
import { DelayAnimation } from './styles';
import { WithAnimationDelayProps } from './types';

/**
 * Used to create a delay between Messages
 * @param props
 * @returns
 */
const WithDelayedAnimation = (props: PropsWithChildren<WithAnimationDelayProps>) => {
  const {
    children,
    relativeIndex = 0,
    animate = false,
    isForcedSystemTypingAnimation = false,
    showIndicator = true,
    duration = DEFAULT_TYPING_ANIMATION_DURATION,
  } = props;

  const { conversationState } = useConversation();
  const { viewState } = useView();
  const [animated, setAnimated] = useState(false);

  const getAnimatedTypingContent = (isAutoScrollToBottom: boolean) => {
    if (!animate || animated) {
      if (showIndicator)
        return (
          <TypingIndicator
            animate={animate}
            duration={duration}
            isAutoScrollToBottom={isAutoScrollToBottom}
            relativeIndex={relativeIndex}
          >
            {children}
          </TypingIndicator>
        );

      return children;
    }
    return (
      <DelayAnimation
        animate={!animated && animate} // Prevent animations from retriggering
        id={htmlId(`delay-animation-${relativeIndex}`)}
        index={relativeIndex}
        onAnimationEnd={() => setAnimated(true)}
      />
    );
  };

  // Show animation for first nested message only
  if (relativeIndex === 0 || isForcedSystemTypingAnimation) {
    // Behavior differs between Advocate and System. Without this, may see "half" animation for Advocate messages.
    const isAdvocateInConvo = conversationState.participants.advocates.length > 0;
    const isAutoScrollNextMsg = viewState.scrollToBottom.isAutoScrollNextMsg;
    const isAdvocateAndScrollNext = isAdvocateInConvo && isAutoScrollNextMsg;
    return getAnimatedTypingContent(isAdvocateAndScrollNext);
  }
  return children;
};

// Prevent animations from re-triggering
const WithDelayedAnimationMemo = memo(WithDelayedAnimation);

export default WithDelayedAnimationMemo;
